import { useState, useEffect } from "react";
import { trpc } from "../lib/trpc.js";
import {
  Scale, FileText, AlertTriangle, CheckCircle2, XCircle,
  Clock, ChevronDown, ChevronRight, RefreshCw, Edit3,
  Save, X, Eye, Filter, Shield, BookOpen, Gavel,
  MessageSquare, Bot, History, Loader2, ScrollText,
  Wand2, RotateCcw, Check, Hash, AlertCircle,
} from "lucide-react";

// ─── Types ─────────────────────────────────────────────────────────────────
type TabId = "statute" | "constitution" | "requests" | "audit";
type RequestStatus = "pending" | "approved" | "rejected" | "expired";

const STATUS_CONFIG: Record<RequestStatus, { label: string; color: string; bgColor: string; icon: typeof Clock }> = {
  pending: { label: "Pendente", color: "text-yellow-400", bgColor: "bg-yellow-500/10 border-yellow-500/20", icon: Clock },
  approved: { label: "Aprovado", color: "text-emerald-400", bgColor: "bg-emerald-500/10 border-emerald-500/20", icon: CheckCircle2 },
  rejected: { label: "Rejeitado", color: "text-red-400", bgColor: "bg-red-500/10 border-red-500/20", icon: XCircle },
  expired: { label: "Expirado", color: "text-muted-foreground", bgColor: "bg-muted/30 border-border", icon: Clock },
};

const TYPE_LABELS: Record<string, string> = {
  exception: "Exceção Temporária",
  amendment: "Alteração do Estatuto",
  clarification: "Esclarecimento",
};

// ─── Main Component ────────────────────────────────────────────────────────
export function Governance() {
  const [activeTab, setActiveTab] = useState<TabId>("statute");
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [editReason, setEditReason] = useState("");
  const [expandedLaw, setExpandedLaw] = useState<number | null>(null);
  const [expandedRequest, setExpandedRequest] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [resolveNote, setResolveNote] = useState("");
  const [loadingRaw, setLoadingRaw] = useState(false);

  const utils = trpc.useUtils();

  // ── Governance Queries ────────────────────────────────────────────────
  const { data: summary, isLoading: loadingSummary } = trpc.governance.getSummary.useQuery();
  const { data: statute } = trpc.governance.getStatute.useQuery();
  const { data: requestsData, isLoading: loadingRequests } = trpc.governance.listRequests.useQuery(
    statusFilter !== "all" ? { status: statusFilter as RequestStatus } : undefined
  );
  const { data: auditData } = trpc.governance.getAuditLog.useQuery(
    { limit: 100 },
    { enabled: activeTab === "audit" }
  );

  // ── Governance Mutations ──────────────────────────────────────────────
  const updateStatuteMut = trpc.governance.updateStatute.useMutation({
    onSuccess: () => {
      utils.governance.invalidate();
      setEditMode(false);
      setEditContent("");
      setEditReason("");
    },
  });

  const resolveRequestMut = trpc.governance.resolveRequest.useMutation({
    onSuccess: () => {
      utils.governance.invalidate();
      setResolveNote("");
      setExpandedRequest(null);
    },
  });

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleStartEdit = async () => {
    setLoadingRaw(true);
    try {
      const rawData = await utils.governance.getStatuteRaw.fetch();
      if (rawData?.raw_text) {
        setEditContent(rawData.raw_text);
      }
    } catch (err) {
      console.error("Erro ao carregar estatuto para edição:", err);
      if (statute?.laws) {
        const fallbackText = Object.values(statute.laws)
          .sort((a: any, b: any) => a.number - b.number)
          .map((law: any) => law.full_text || `## Lei ${law.number}: ${law.title}`)
          .join("\n\n---\n\n");
        setEditContent(fallbackText);
      }
    } finally {
      setLoadingRaw(false);
      setEditMode(true);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditContent("");
    setEditReason("");
  };

  const handleSaveStatute = () => {
    if (!editContent.trim() || !editReason.trim()) return;
    updateStatuteMut.mutate({ content: editContent, reason: editReason });
  };

  const tabs: { id: TabId; label: string; icon: typeof Scale }[] = [
    { id: "statute", label: "Estatuto Brain", icon: BookOpen },
    { id: "constitution", label: "Constituição Org", icon: ScrollText },
    { id: "requests", label: "Solicitações", icon: MessageSquare },
    { id: "audit", label: "Auditoria", icon: History },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Scale className="w-7 h-7 text-primary" />
            Governança
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Estatuto do Brain, Constituição da Organização e Sistema de Solicitações
          </p>
        </div>
        <button
          onClick={() => utils.governance.invalidate()}
          className="flex items-center gap-2 px-3 py-2 bg-card border border-border hover:bg-muted/50 rounded-lg transition-colors text-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="text-sm text-muted-foreground">Versão</div>
            <div className="text-2xl font-bold text-foreground">{summary.version}</div>
          </div>
          <div className="bg-card border border-primary/20 rounded-xl p-4">
            <div className="text-sm text-primary">Leis</div>
            <div className="text-2xl font-bold text-primary">{summary.laws?.length || 0}</div>
          </div>
          <div className="bg-card border border-yellow-500/20 rounded-xl p-4">
            <div className="text-sm text-yellow-400">Pendentes</div>
            <div className="text-2xl font-bold text-yellow-400">{summary.pending_requests || 0}</div>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="text-sm text-muted-foreground">Hash</div>
            <div className="text-sm font-mono text-muted-foreground truncate">{summary.hash?.slice(0, 12)}...</div>
          </div>
        </div>
      )}

      {loadingSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-card border border-border rounded-xl p-4 animate-pulse">
              <div className="h-4 w-16 bg-muted rounded mb-2" />
              <div className="h-8 w-12 bg-muted rounded" />
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-card border border-border rounded-xl p-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-1 justify-center ${
              activeTab === tab.id
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.id === "requests" && (summary?.pending_requests || 0) > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-yellow-500/20 text-yellow-400">
                {summary?.pending_requests}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab: Estatuto Brain ──────────────────────────────────────────── */}
      {activeTab === "statute" && (
        <StatuteTab
          statute={statute}
          summary={summary}
          loadingSummary={loadingSummary}
          editMode={editMode}
          editContent={editContent}
          editReason={editReason}
          loadingRaw={loadingRaw}
          expandedLaw={expandedLaw}
          updateStatuteMut={updateStatuteMut}
          onStartEdit={handleStartEdit}
          onCancelEdit={handleCancelEdit}
          onSaveStatute={handleSaveStatute}
          onEditContentChange={setEditContent}
          onEditReasonChange={setEditReason}
          onExpandLaw={setExpandedLaw}
        />
      )}

      {/* ── Tab: Constituição Org ────────────────────────────────────────── */}
      {activeTab === "constitution" && <ConstitutionTab />}

      {/* ── Tab: Requests ────────────────────────────────────────────────── */}
      {activeTab === "requests" && (
        <RequestsTab
          requestsData={requestsData}
          loadingRequests={loadingRequests}
          statusFilter={statusFilter}
          expandedRequest={expandedRequest}
          resolveNote={resolveNote}
          resolveRequestMut={resolveRequestMut}
          onStatusFilterChange={setStatusFilter}
          onExpandRequest={setExpandedRequest}
          onResolveNoteChange={setResolveNote}
        />
      )}

      {/* ── Tab: Auditoria ───────────────────────────────────────────────── */}
      {activeTab === "audit" && <AuditTab auditData={auditData} />}
    </div>
  );
}

// ─── Statute Tab ────────────────────────────────────────────────────────────
function StatuteTab({
  statute, summary, loadingSummary, editMode, editContent, editReason,
  loadingRaw, expandedLaw, updateStatuteMut,
  onStartEdit, onCancelEdit, onSaveStatute,
  onEditContentChange, onEditReasonChange, onExpandLaw,
}: any) {
  return (
    <div className="space-y-4">
      {/* Edit Mode Toggle */}
      <div className="flex justify-end gap-2">
        {!editMode ? (
          <button
            onClick={onStartEdit}
            disabled={loadingRaw}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/80 text-primary-foreground rounded-lg transition-colors text-sm disabled:opacity-50"
          >
            {loadingRaw ? <Loader2 className="w-4 h-4 animate-spin" /> : <Edit3 className="w-4 h-4" />}
            {loadingRaw ? "Carregando..." : "Editar Estatuto"}
          </button>
        ) : (
          <>
            <button onClick={onCancelEdit} className="flex items-center gap-2 px-4 py-2 bg-card border border-border hover:bg-muted/50 rounded-lg transition-colors text-sm">
              <X className="w-4 h-4" /> Cancelar
            </button>
            <button
              onClick={onSaveStatute}
              disabled={!editContent.trim() || !editReason.trim() || updateStatuteMut.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors text-sm disabled:opacity-50"
            >
              {updateStatuteMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salvar Alterações
            </button>
          </>
        )}
      </div>

      {/* Edit Mode */}
      {editMode && (
        <div className="space-y-4">
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
            <div className="flex items-center gap-2 text-blue-400 text-sm font-medium mb-1">
              <AlertTriangle className="w-4 h-4" /> Modo de Edição Ativo
            </div>
            <p className="text-xs text-blue-400/70">
              Você está editando o Estatuto Inegociável do YOS. Todas as alterações serão registradas no log de auditoria.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Motivo da Alteração <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={editReason}
              onChange={(e) => onEditReasonChange(e.target.value)}
              placeholder="Ex: Adicionar regra sobre uso de APIs externas"
              className="w-full px-4 py-2 bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Conteúdo do Estatuto (Markdown) <span className="text-red-400">*</span>
            </label>
            <textarea
              value={editContent}
              onChange={(e) => onEditContentChange(e.target.value)}
              rows={35}
              className="w-full px-4 py-3 bg-card border border-border rounded-xl text-foreground font-mono text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary/50 resize-y min-h-[500px]"
              placeholder="Carregando conteúdo do estatuto..."
            />
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span>{editContent.length} caracteres</span>
              <span>Formato: Markdown</span>
            </div>
          </div>
          {updateStatuteMut.isError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
              <div className="flex items-center gap-2 text-red-400 text-sm font-medium">
                <XCircle className="w-4 h-4" />
                Erro ao salvar: {updateStatuteMut.error?.message || "Erro desconhecido"}
              </div>
            </div>
          )}
        </div>
      )}

      {/* View Mode — Laws */}
      {!editMode && statute && (
        <div className="space-y-3">
          {Object.values(statute.laws)
            .sort((a: any, b: any) => a.number - b.number)
            .map((law: any) => {
              const isExpanded = expandedLaw === law.number;
              return (
                <div key={law.number} className="bg-card border border-border rounded-xl overflow-hidden">
                  <button
                    onClick={() => onExpandLaw(isExpanded ? null : law.number)}
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary font-bold text-sm shrink-0">
                      {law.number}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-foreground">{law.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {law.nevers?.length || 0} proibições · {law.always?.length || 0} obrigações
                      </div>
                    </div>
                    {isExpanded ? <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0" /> : <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />}
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-3">
                      {law.nevers?.length > 0 && (
                        <div>
                          <div className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                            <XCircle className="w-3 h-3" /> Proibições (NUNCA)
                          </div>
                          <ul className="space-y-1">
                            {law.nevers.map((n: string, i: number) => (
                              <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                                <span className="text-red-400 mt-0.5 shrink-0">✕</span><span>{n}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {law.always?.length > 0 && (
                        <div>
                          <div className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> Obrigações (SEMPRE)
                          </div>
                          <ul className="space-y-1">
                            {law.always.map((a: string, i: number) => (
                              <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                                <span className="text-emerald-400 mt-0.5 shrink-0">✓</span><span>{a}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {law.full_text && (
                        <details className="mt-2">
                          <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">Ver texto completo</summary>
                          <pre className="mt-2 p-3 bg-muted/30 rounded-lg text-xs text-muted-foreground whitespace-pre-wrap font-mono">{law.full_text}</pre>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}

      {!editMode && !statute && !loadingSummary && (
        <div className="bg-card border border-border rounded-xl p-8 text-center">
          <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Estatuto não encontrado. Verifique se o ESTATUTO.md existe no repositório do Brain.</p>
        </div>
      )}
    </div>
  );
}

// ─── Constitution Tab (Organization-level) ──────────────────────────────────
function ConstitutionTab() {
  const { data, isLoading } = trpc.constitution.get.useQuery();
  const utils = trpc.useUtils();
  const [content, setContent] = useState("");
  const [tone, setTone] = useState("formal");
  const [language, setLanguage] = useState("pt-BR");
  const [saved, setSaved] = useState(false);
  const [showGenerator, setShowGenerator] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [industry, setIndustry] = useState("");

  useEffect(() => {
    if (data?.content) {
      setContent(data.content);
      if (data.tone) setTone(data.tone);
      if (data.language) setLanguage(data.language);
    }
  }, [data]);

  const saveMutation = trpc.constitution.save.useMutation({
    onSuccess: () => {
      utils.constitution.get.invalidate();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const generateMutation = trpc.constitution.generateTemplate.useMutation({
    onSuccess: (result) => setContent(result.template),
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-muted rounded w-1/3 animate-pulse" />
        <div className="h-96 bg-card border border-border rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Defina as regras, tom e diretrizes que todos os agentes da sua organização devem seguir
        </p>
        {data?.exists && (
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Hash className="w-3 h-3" />v{data.version}</span>
            {data.updatedAt && (
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(data.updatedAt).toLocaleDateString("pt-BR")}</span>
            )}
          </div>
        )}
      </div>

      {/* Quick settings */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2">
          <span className="text-xs text-muted-foreground">Tom:</span>
          {["formal", "casual", "technical"].map((t) => (
            <button
              key={t}
              onClick={() => setTone(t)}
              className={`text-xs px-2 py-1 rounded transition-colors ${
                tone === t ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t === "formal" ? "Formal" : t === "casual" ? "Casual" : "Técnico"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2">
          <span className="text-xs text-muted-foreground">Idioma:</span>
          {[{ id: "pt-BR", label: "PT-BR" }, { id: "en-US", label: "EN" }, { id: "es", label: "ES" }].map((l) => (
            <button
              key={l.id}
              onClick={() => setLanguage(l.id)}
              className={`text-xs px-2 py-1 rounded transition-colors ${
                language === l.id ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowGenerator(!showGenerator)}
          className="flex items-center gap-1.5 text-xs px-3 py-2 bg-card border border-border rounded-lg text-muted-foreground hover:text-primary hover:border-primary/30 transition-colors"
        >
          <Wand2 className="w-3.5 h-3.5" /> Gerar Template
        </button>
      </div>

      {/* Template generator */}
      {showGenerator && (
        <div className="bg-card border border-primary/20 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-primary" /> Gerar Template de Constituição
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Nome da Empresa</label>
              <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Ex: Ymonetize"
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Setor</label>
              <input type="text" value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="Ex: Marketing Digital"
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
          </div>
          <button
            onClick={() => generateMutation.mutate({ companyName, industry, tone: tone as any, language })}
            disabled={!companyName || !industry || generateMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {generateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            Gerar
          </button>
        </div>
      )}

      {/* Editor */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-secondary/30">
          <span className="text-xs text-muted-foreground flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Editor Markdown</span>
          <span className="text-xs text-muted-foreground">{content.length.toLocaleString()} caracteres</span>
        </div>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="# Constituição da Empresa&#10;&#10;Defina aqui as regras e diretrizes para os agentes de IA da sua organização...&#10;&#10;## Identidade&#10;- Nome da empresa&#10;- Setor de atuação&#10;- Tom de comunicação&#10;&#10;## Diretrizes&#10;1. ...&#10;&#10;## Restrições&#10;- ..."
          className="w-full min-h-[500px] p-4 bg-card text-foreground text-sm font-mono leading-relaxed resize-y focus:outline-none placeholder:text-muted-foreground/50"
        />
      </div>

      {/* Save bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {!data?.exists && (
            <div className="flex items-center gap-1.5 text-xs text-amber-500">
              <AlertCircle className="w-3.5 h-3.5" /> Nenhuma constituição salva ainda
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {data?.exists && (
            <button onClick={() => setContent(data.content || "")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <RotateCcw className="w-4 h-4" /> Reverter
            </button>
          )}
          <button
            onClick={() => saveMutation.mutate({ content })}
            disabled={saveMutation.isPending || content.length < 10}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            {saved ? "Salvo!" : "Salvar Constituição"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Requests Tab ───────────────────────────────────────────────────────────
function RequestsTab({
  requestsData, loadingRequests, statusFilter, expandedRequest,
  resolveNote, resolveRequestMut, onStatusFilterChange, onExpandRequest, onResolveNoteChange,
}: any) {
  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-muted-foreground" />
        {["all", "pending", "approved", "rejected"].map((s) => (
          <button
            key={s}
            onClick={() => onStatusFilterChange(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === s
                ? "bg-primary text-primary-foreground"
                : "bg-card border border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {s === "all" ? "Todos" : STATUS_CONFIG[s as RequestStatus]?.label || s}
          </button>
        ))}
        {requestsData?.stats && (
          <span className="ml-auto text-xs text-muted-foreground">{requestsData.stats.total} total</span>
        )}
      </div>

      {loadingRequests && (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-card border border-border rounded-xl p-4 animate-pulse">
              <div className="h-5 w-48 bg-muted rounded mb-2" />
              <div className="h-4 w-32 bg-muted rounded" />
            </div>
          ))}
        </div>
      )}

      {requestsData?.requests?.length === 0 && (
        <div className="bg-card border border-border rounded-xl p-8 text-center">
          <Gavel className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">
            {statusFilter === "pending" ? "Nenhuma solicitação pendente" : "Nenhuma solicitação encontrada"}
          </p>
        </div>
      )}

      {requestsData?.requests?.map((req: any) => {
        const isExpanded = expandedRequest === req.id;
        const statusCfg = STATUS_CONFIG[req.status as RequestStatus] || STATUS_CONFIG.pending;
        const StatusIcon = statusCfg.icon;

        return (
          <div key={req.id} className={`bg-card border rounded-xl overflow-hidden ${statusCfg.bgColor}`}>
            <button
              onClick={() => onExpandRequest(isExpanded ? null : req.id)}
              className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/20 transition-colors"
            >
              <StatusIcon className={`w-5 h-5 ${statusCfg.color} shrink-0`} />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-foreground flex items-center gap-2">
                  {req.title}
                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted/50 text-muted-foreground">Lei {req.law_number}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                  <Bot className="w-3 h-3" />{req.agent_id}<span>·</span>{TYPE_LABELS[req.type] || req.type}<span>·</span>{new Date(req.created_at).toLocaleDateString("pt-BR")}
                </div>
              </div>
              <span className={`text-xs font-medium ${statusCfg.color}`}>{statusCfg.label}</span>
              {isExpanded ? <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0" /> : <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />}
            </button>

            {isExpanded && (
              <div className="px-4 pb-4 space-y-3 border-t border-border/50">
                <div className="pt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Descrição</div>
                    <p className="text-sm text-foreground">{req.description}</p>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Justificativa</div>
                    <p className="text-sm text-foreground">{req.justification}</p>
                  </div>
                </div>
                {req.proposed_action && (
                  <div>
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Ação Proposta</div>
                    <p className="text-sm text-foreground">{req.proposed_action}</p>
                  </div>
                )}
                {req.resolution_note && (
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Nota de Resolução ({req.resolved_by})</div>
                    <p className="text-sm text-foreground">{req.resolution_note}</p>
                  </div>
                )}
                {req.status === "pending" && (
                  <div className="pt-2 border-t border-border/50 space-y-3">
                    <input
                      type="text"
                      value={resolveNote}
                      onChange={(e) => onResolveNoteChange(e.target.value)}
                      placeholder="Nota de resolução (opcional)"
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => resolveRequestMut.mutate({ requestId: req.id, approved: true, note: resolveNote })}
                        disabled={resolveRequestMut.isPending}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors text-sm disabled:opacity-50"
                      >
                        <CheckCircle2 className="w-4 h-4" /> Aprovar
                      </button>
                      <button
                        onClick={() => resolveRequestMut.mutate({ requestId: req.id, approved: false, note: resolveNote })}
                        disabled={resolveRequestMut.isPending}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors text-sm disabled:opacity-50"
                      >
                        <XCircle className="w-4 h-4" /> Rejeitar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {requestsData?.stats && requestsData.stats.total > 0 && (
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Estatísticas</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(requestsData.stats.by_status).map(([status, count]) => {
              const cfg = STATUS_CONFIG[status as RequestStatus];
              return cfg ? (
                <div key={status} className="text-center">
                  <div className={`text-lg font-bold ${cfg.color}`}>{count as number}</div>
                  <div className="text-xs text-muted-foreground">{cfg.label}</div>
                </div>
              ) : null;
            })}
          </div>
          {Object.keys(requestsData.stats.by_agent).length > 0 && (
            <div className="mt-3 pt-3 border-t border-border/50">
              <div className="text-xs text-muted-foreground mb-2">Por Agente:</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(requestsData.stats.by_agent).map(([agent, count]) => (
                  <span key={agent} className="px-2 py-1 bg-muted/30 rounded-lg text-xs text-muted-foreground">
                    <Bot className="w-3 h-3 inline mr-1" />{agent}: {count as number}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Audit Tab ──────────────────────────────────────────────────────────────
function AuditTab({ auditData }: any) {
  return (
    <div className="space-y-3">
      {auditData?.entries?.length === 0 && (
        <div className="bg-card border border-border rounded-xl p-8 text-center">
          <History className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Nenhum registro de auditoria encontrado</p>
        </div>
      )}

      {auditData?.entries?.map((entry: any, i: number) => (
        <div key={i} className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full shrink-0 ${
              entry.action?.includes("blocked") ? "bg-red-400" :
              entry.action?.includes("approved") ? "bg-emerald-400" :
              entry.action?.includes("update") ? "bg-blue-400" :
              "bg-muted-foreground"
            }`} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-foreground font-medium">{entry.action || "Ação desconhecida"}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {entry.agent_id && <span className="mr-2"><Bot className="w-3 h-3 inline" /> {entry.agent_id}</span>}
                {entry.timestamp && new Date(entry.timestamp).toLocaleString("pt-BR")}
              </div>
            </div>
            {entry.law_number && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-muted/50 text-muted-foreground">Lei {entry.law_number}</span>
            )}
          </div>
          {entry.details && <p className="text-xs text-muted-foreground mt-2 pl-5">{entry.details}</p>}
        </div>
      ))}

      {auditData && (
        <div className="text-center text-xs text-muted-foreground">{auditData.total} registros no total</div>
      )}
    </div>
  );
}
